import { FilterOption } from '../components/custom-input/FilterDropdown';
import { StrawRow } from '../components/embryo-table/EmbryoDetailsTable';
import { ValidatedState } from '../components/custom-input/ValidatedInput';
import { onFarmDepositPost } from '../api/onFarmTransactionApi';
import { specimenUOM } from '../types/enums';
import { OnFarmAnimal, SpecimenType, StorageLocation } from '../types/interfaces';
import {
  OnFarmDepositRequestBody,
  OnFarmEmbryoStrawRequest,
  OnFarmSpecimenLocationRequest,
} from '../types/interfaces/onFarmTransaction.interfaces';

export type OnFarmDepositSelectedStates = {
  accountId?: number;
  animal?: OnFarmAnimal;
  specimensType?: FilterOption<SpecimenType>;
  specimenUOM?: FilterOption<specimenUOM>;
  itemType?: ValidatedState;
  freezeDate?: Date;
  lotNumber?: ValidatedState;
  collectionSite?: ValidatedState;
  collectedBy?: ValidatedState;
  processedBy?: ValidatedState;
  frozenBy?: ValidatedState;
  quantity?: ValidatedState;
  notes?: string;
  storageLocations?: StorageLocation[];
  embryoStraws?: StrawRow[];
};

const isEmbryoDeposit = (selectedStates: OnFarmDepositSelectedStates) =>
  selectedStates.specimensType?.name === 'Embryo' || selectedStates.itemType?.value === 'Embryo';

const hasValidEmbryoStraws = (selectedStates: OnFarmDepositSelectedStates) => {
  const embryoStraws = selectedStates.embryoStraws ?? [];
  if (embryoStraws.length === 0) {
    return false;
  }

  return embryoStraws.every(
    straw => !!straw.embryoStrawNumber && Number(straw.embryoCountPerStraw) > 0 && !!straw.storageCanisterId,
  );
};

export const isOnFarmDepositValid = (selectedStates: OnFarmDepositSelectedStates) => {
  const {
    accountId,
    animal,
    specimensType,
    specimenUOM: selectedSpecimenUOM,
    itemType,
    freezeDate,
    quantity,
    storageLocations,
  } = selectedStates;

  if (
    !accountId ||
    !animal ||
    !specimensType?.value?.specimenTypeId ||
    !selectedSpecimenUOM?.value ||
    !itemType?.valid ||
    !itemType?.value ||
    !freezeDate ||
    !quantity?.valid ||
    !quantity?.value ||
    Number(quantity.value) <= 0
  ) {
    return false;
  }

  const totalStorageLocationQuantities = storageLocations?.reduce(
    (total, location) => total + Number(location.quantity.value),
    0,
  );
  if (totalStorageLocationQuantities !== Number(quantity.value)) {
    return false;
  }

  const hasInvalidStorageLocation = storageLocations?.some(
    location =>
      !location?.site.value ||
      !location?.tank.value ||
      !location?.canister.value ||
      !location?.quantity.value ||
      Number(location.quantity.value) <= 0,
  );

  if (hasInvalidStorageLocation) {
    return false;
  }

  if (isEmbryoDeposit(selectedStates) && !hasValidEmbryoStraws(selectedStates)) {
    return false;
  }

  return true;
};

const mapEmbryoStrawsToRequest = (selectedStates: OnFarmDepositSelectedStates): OnFarmEmbryoStrawRequest[] => {
  return (selectedStates.embryoStraws ?? []).map(straw => ({
    embryoStrawNumber: straw.embryoStrawNumber,
    embryoGrade: straw.embryoGrade ?? '',
    embryoStage: straw.embryoStage ?? '',
    embryoCountPerStraw: Number(straw.embryoCountPerStraw),
    accountStorageCanisterId: Number(straw.storageCanisterId),
  }));
};

export const mapStrawsToStorageCanisters = (embryoStraws: StrawRow[], storageLocations: StorageLocation[] = []): StrawRow[] => {
  return embryoStraws.map(straw => {
    let matchedCanisterId: number | undefined;

    storageLocations.forEach(location => {
      const strawCodes = location.strawCodesText?.split(',').map(id => id.trim()) ?? [];
      if (strawCodes.includes(straw.embryoStrawNumber)) {
        matchedCanisterId = location.canister?.value ? Number(location.canister.value) : undefined;
      }
    });

    return {
      ...straw,
      storageCanisterId: matchedCanisterId,
    };
  });
};

export const createOnFarmDepositTransaction = async (selectedStates: OnFarmDepositSelectedStates) => {
  try {
    if (!isOnFarmDepositValid(selectedStates) || !selectedStates.accountId) {
      throw new Error('Invalid OnFarm Deposit Transaction');
    }

    const onFarmSpecimenLocations: OnFarmSpecimenLocationRequest[] =
      selectedStates.storageLocations?.map((sl: StorageLocation) => ({
        quantity: Number(sl.quantity.value!),
        accountStorageCanisterId: Number(sl.canister.value),
      })) ?? [];

    const embryoPeople = isEmbryoDeposit(selectedStates)
      ? {
          veterinarianName: selectedStates.collectedBy?.value || undefined,
          embryologistName: selectedStates.processedBy?.value || undefined,
          technicianName: selectedStates.frozenBy?.value || undefined,
        }
      : {};

    const animal = selectedStates.animal!;
    const onFarmAnimalId = animal.onFarmAnimalId ?? animal.animalId;
    if (!onFarmAnimalId) {
      throw new Error('Invalid OnFarm Deposit Transaction: missing animal id');
    }

    const payload: OnFarmDepositRequestBody = {
      onFarmAnimalId,
      specimenTypeId: selectedStates.specimensType!.value.specimenTypeId!,
      specimenUOM: selectedStates.specimenUOM!.value,
      originalQuantity: Number(selectedStates.quantity!.value!),
      freezeDate: selectedStates.freezeDate!,
      lotNumber: selectedStates.lotNumber?.value || undefined,
      notes: selectedStates.notes || undefined,
      onFarmSpecimenLocations,
      ...embryoPeople,
      ...(isEmbryoDeposit(selectedStates) ? { onFarmEmbryoStraws: mapEmbryoStrawsToRequest(selectedStates) } : {}),
    };

    await onFarmDepositPost(selectedStates.accountId, payload);
  } catch (error) {
    throw error;
  }
};
